import React, {useRef} from 'react';
import clsx from 'clsx';
import {useTranslation} from 'react-i18next';
import {ErrorType} from 'utils/types';
import {useCombinedRef, useElementScope} from '@chekin/core';
import removeIcon from 'assets/icons/remove.svg?no-inline';
import paperClipIcon from 'assets/icons/paper-clip.svg?no-inline';
import {ErrorMessage, Fieldset} from '@guestapp/old-ui-kit';
import {
  FileInputLabel,
  HiddenFileUploader,
  FileInputBox,
  ClipContainer,
  FileName,
  FileNameContainer,
  ClearIcon,
  ClearButton,
} from './styled';

export type FileInputProps = {
  onChange?: (e: React.ChangeEvent<HTMLInputElement> | string) => void;
  label?: string;
  value?: File | string;
  placeholder?: string;
  accept?: string;
  disabled?: boolean;
  multiple?: boolean;
  invalid?: boolean;
  className?: string;
  error?: ErrorType;
};

const FileInput = React.forwardRef<HTMLInputElement, FileInputProps>(
  (
    {
      onChange,
      label,
      accept = '',
      disabled,
      className,
      multiple,
      placeholder,
      value,
      error,
      invalid,
    }: FileInputProps,
    ref,
  ) => {
    const internalRef = useRef<HTMLInputElement | null>(null);
    const inputCombinedRef = useCombinedRef(ref, internalRef);
    const {elementRef, elWindow} = useElementScope<HTMLLabelElement>();
    const {t} = useTranslation();
    const handleClear = () => {
      if (onChange) {
        onChange('');
      }
    };

    const handleLabelClick = () => internalRef.current?.click();

    return (
      <FileInputLabel
        ref={elementRef}
        className={clsx(className, 'file-input', {
          'file-input--invalid': invalid || Boolean(error),
          'file-input--disabled': disabled,
        })}
        disabled={disabled}
        $invalid={invalid || Boolean(error)}
      >
        <HiddenFileUploader
          multiple={multiple}
          disabled={disabled}
          type="file"
          name="fileInput"
          accept={accept}
          onChange={onChange}
          ref={inputCombinedRef}
        />
        <Fieldset
          isFocused={false}
          invalid={!!error}
          isEmpty={!value}
          isActivated={!!value}
          htmlFor={placeholder}
          legend={label}
          label={label}
          onClick={handleLabelClick}
        />
        {/* <Label>{label}</Label> */}
        <FileInputBox $isEmpty={!!value} className="file-input__box kiosko:h-[106px]">
          {value instanceof elWindow.File && (
            <FileNameContainer onClick={e => e.preventDefault()}>
              <FileName className="file-input__filename">{value.name}</FileName>
              <ClearButton
                disabled={disabled}
                onClick={handleClear}
                className="file-input__clear"
              >
                <ClearIcon src={removeIcon} alt="Remove" />
              </ClearButton>
            </FileNameContainer>
          )}
          <ClipContainer className="file-input__clip kiosko:w-12 kiosko:h-12">
            <img
              src={paperClipIcon}
              alt={t('attach_file')}
              className="kiosko:w-12 kiosko:h-12"
            />
          </ClipContainer>
        </FileInputBox>
        {error && <ErrorMessage>{error as string}</ErrorMessage>}
      </FileInputLabel>
    );
  },
);

export {FileInput};
